import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../utils/redux/hooks";
import { RootState } from "../../utils/redux/store";

/** The session lives in an httpOnly cookie this code cannot read, so the guard
 *  cannot inspect a credential. It checks a UI-level marker instead:
 *
 *  - redux `isLoggedIn.authenticated`, set on a successful login
 *  - falling back to a persisted marker, so a page REFRESH does not log you out
 *    (redux-persist is disabled here, so redux alone is memory-only, while the
 *    cookie survives — without this the browser holds a valid session and the
 *    panel shows a login screen)
 *
 *  Neither is proof of anything: they say "the UI believes there is a session".
 *  The cookie is the real credential, and if it has gone the first API call
 *  401s. That is the correct division — a client cannot verify its own auth. */
const ProtectedRoute = ({ children }: any) => {
  const loggedIn = useAppSelector((store: RootState) => store.user.isLoggedIn);
  const location = useLocation();
  const user = useAppSelector((store: RootState) => store.user.userData) as any;

  const hasSession =
    Boolean(loggedIn?.authenticated) ||
    (typeof window !== "undefined" &&
      window.localStorage.getItem("pw_admin_session") === "1");

  if (hasSession) {
    if (!user?.accountType) return <p role="status">Checking staff permissions…</p>;
    if (location.pathname === '/field-work/telemarketer') return <Navigate to={`/telemarketing${location.search}`} replace />;
    if (user.accountType === 'telemarketer' && location.pathname !== '/telemarketing') return <Navigate to="/telemarketing" replace />;
    if (user.accountType === 'marketer' && location.pathname === '/telemarketing') return user.telemarketingEnabled === true ? children : <Navigate to="/field-work" replace />;
    if (user.accountType === 'marketer' && !/^\/(?:field-work)(?:\/|$)/.test(location.pathname)) return <Navigate to="/field-work" replace />;
    return children;
  }
  return <Navigate to="/login" state={{ from: location }} replace />;
};

export default ProtectedRoute;
